import React, {FC, useState} from 'react';

import avatar from 'assets/images/svg/avatar.svg';
import cone from 'assets/images/svg/cone-logo-mobile.svg';
import curvedArrow from 'assets/images/svg/curved-arrow.svg';
import moment from 'moment/moment';

import {Comment as ApiComment} from '../../../../api-client';
import {formatDateTime} from '../../../../formatters';
import {Image} from '../../../common';
import CommentForm from '../CommentForm';
import styles from './Comment.module.scss';

interface Props {
  comment: ApiComment;
  projectUuid: string;
  onCommentCreated?: () => void;
  chatAvailable?: boolean;
}

const VISIBLE_REPLIES = 2;

const Comment: FC<Props> = ({comment, projectUuid, onCommentCreated, chatAvailable}) => {
  const createdAt = moment(comment.createdAt).toDate();
  const [isReplyOpen, setIsReplyOpen] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const totalReplies = comment.replies.length;
  const hasMoreReplies = totalReplies > VISIBLE_REPLIES;

  const renderReplies = (replies: ApiComment[]) =>
    replies.map((reply) => (
      <li key={reply.uuid} className={styles.replyItem}>
        <Comment projectUuid={projectUuid} comment={reply} onCommentCreated={onCommentCreated} />
      </li>
    ));

  const handleReplyClick = () => {
    setIsReplyOpen(!isReplyOpen);
  };

  const handleShowAllClick = () => {
    setIsExpanded(true);
  };

  const handleCollapseClick = () => {
    setIsExpanded(false);
  };

  return (
    <div className={styles.comment}>
      <div className={styles.header}>
        <Image
          className={styles.avatar}
          src={comment.author.photoUrl ? comment.author.photoUrl : avatar}
          alt={'avatar'}
        />

        <div className={styles.headerInfo}>
          <div className={styles.authorNameContainer}>
            <div className={styles.authorName}>{comment.author.name}</div>

            {comment.author.isAdmin && (
              <div className={styles.authorBadgeContainer}>
                <Image className={styles.cone} src={cone} alt={'cone'} />
                <span className={styles.authorBadge}>{comment.author?.department}</span>
              </div>
            )}
          </div>

          <div className={styles.date}>{formatDateTime(createdAt, 'DD.MM.YYYY')}</div>
        </div>
      </div>

      <div className={styles.content}>{comment.text}</div>

      <div className={styles.replies}>
        {comment.isParent && (
          <>
            {chatAvailable && (
              <div className={styles.repliesAnswer} onClick={handleReplyClick}>
                <Image src={curvedArrow} alt="-->" />
                {isReplyOpen ? 'Скрыть' : 'Ответить'}
                {totalReplies > 0 && !isReplyOpen && ` (${totalReplies})`}
              </div>
            )}

            {isReplyOpen && (
              <div className={styles.threadContainer}>
                {totalReplies > 0 && (
                  <ul className={styles.threadReplies}>{renderReplies(comment.replies)}</ul>
                )}
                <div className={styles.threadForm}>
                  {chatAvailable && (
                    <CommentForm
                      projectUuid={projectUuid}
                      parentUuid={comment.uuid}
                      onCreated={onCommentCreated}
                      autoFocus
                    />
                  )}
                  <div className={styles.showAllButton} onClick={handleCollapseClick}>
                    Скрыть
                  </div>
                </div>
              </div>
            )}
          </>
        )}

        {!isReplyOpen && totalReplies > 0 && (
          <>
            <ul className={styles.repliesList}>
              {renderReplies(
                isExpanded ? comment.replies : comment.replies.slice(0, VISIBLE_REPLIES)
              )}
            </ul>
            {hasMoreReplies && !isExpanded && (
              <div className={styles.showAllButton} onClick={handleShowAllClick}>
                Показать все сообщения ({totalReplies})
              </div>
            )}
            {hasMoreReplies && isExpanded && (
              <>
                <div className={styles.threadForm}>
                  {chatAvailable && (
                    <CommentForm
                      projectUuid={projectUuid}
                      parentUuid={comment.uuid}
                      onCreated={onCommentCreated}
                      autoFocus
                    />
                  )}
                </div>
                <div className={styles.showAllButton} onClick={handleCollapseClick}>
                  Скрыть
                </div>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default Comment;
